<template>
  <div class="upload-files-wrap">
    <div class="upload-files-header">
      <h3 class="upload-files-title">{{ $t('FILES.TITLE') }}</h3>
      <button-v2
        size="sm"
        variant="filled"
        icon="upload"
        @click="showUploadModal = true"
      >
        {{ $t('FILES.UPLOAD_BUTTON') }}
      </button-v2>
    </div>

    <table-v2 v-if="files.length" :hover-rows="true" :is-r-t-l="isRTLView">
      <template #header>
        <table-header-cell-v2
          :title="$t('FILES.TABLE.FILE_NAME')"
          :width="compact ? '40%' : 300"
          :align="columnAlign"
          :is-dropdown-open="activeDropdownColumn === 'file_name'"
          @toggle-dropdown="toggleDropdown('file_name')"
          @close-dropdown="closeDropdown"
        >
          <template #dropdown>
            <table-dropdown-v2
              :show-filter="true"
              :filter-label="$t('FILES.DROPDOWN.FILTER_BY')"
              :sort-label="$t('FILES.DROPDOWN.SORT_BY')"
              :search-placeholder="$t('FILES.DROPDOWN.SEARCH_BY_FILE_NAME')"
              :filter-query="columnFilters.file_name"
              :sort-options="getSortOptions()"
              :current-sort="getCurrentSort('file_name')"
              @update:filterQuery="val => setFilter('file_name', val)"
              @sort="order => applySortFromDropdown('file_name', order)"
            />
          </template>
        </table-header-cell-v2>
        <table-header-cell-v2
          :title="$t('FILES.TABLE.COMMENTS')"
          :align="columnAlign"
          :is-dropdown-open="activeDropdownColumn === 'comment'"
          @toggle-dropdown="toggleDropdown('comment')"
          @close-dropdown="closeDropdown"
        >
          <template #dropdown>
            <table-dropdown-v2
              :show-filter="true"
              :filter-label="$t('FILES.DROPDOWN.FILTER_BY')"
              :sort-label="$t('FILES.DROPDOWN.SORT_BY')"
              :search-placeholder="$t('FILES.DROPDOWN.SEARCH_BY_COMMENT')"
              :filter-query="columnFilters.comment"
              :sort-options="getSortOptions()"
              :current-sort="getCurrentSort('comment')"
              @update:filterQuery="val => setFilter('comment', val)"
              @sort="order => applySortFromDropdown('comment', order)"
            />
          </template>
        </table-header-cell-v2>
        <table-header-cell-v2
          :show-menu="false"
          :title="$t('FILES.TABLE.ACTION')"
          :width="120"
          :align="columnAlign"
        />
      </template>
      <template #body>
        <table-row-v2 v-for="file in displayedFiles" :key="file.id">
          <table-cell-v2 :align="columnAlign">
            <div class="uf-name-cell">
              <file-type-icon :file-name="getFileName(file)" :size="32" />
              <a
                v-if="getFileUrl(file)"
                :href="getFileUrl(file)"
                target="_blank"
                rel="noopener noreferrer"
                class="uf-filename-link"
                dir="auto"
              >
                {{ getFileName(file) }}
              </a>
              <span v-else class="uf-filename" dir="auto">{{
                getFileName(file)
              }}</span>
            </div>
          </table-cell-v2>
          <table-cell-v2 :align="columnAlign" class="uf-comment-td">
            <span
              v-if="editingFileId !== file.id"
              class="block truncate"
              dir="auto"
              :title="file.comment || null"
            >
              {{ file.comment || '' }}
            </span>
            <input
              v-else
              ref="commentInput"
              v-model="editComment"
              type="text"
              class="uf-comment-input"
              @keyup.enter="saveComment(file)"
              @keyup.esc="cancelEdit"
              @blur="saveComment(file)"
            />
          </table-cell-v2>
          <table-cell-v2 :align="columnAlign" class="uf-actions-td">
            <div class="uf-actions">
              <button-v2
                variant="ghost"
                size="md"
                icon="edit"
                :title="$t('FILES.ACTIONS.EDIT_COMMENT')"
                @click="startEdit(file)"
              />
              <button-v2
                variant="ghost"
                size="md"
                icon="delete"
                color="danger"
                :title="$t('FILES.ACTIONS.DELETE_FILE')"
                @click="fileToDelete = file"
              />
            </div>
          </table-cell-v2>
        </table-row-v2>
        <table-row-v2 v-if="!displayedFiles.length">
          <table-cell-v2 colspan="3" align="center" class="uf-no-match">
            {{ $t('FILES.NO_MATCH') }}
          </table-cell-v2>
        </table-row-v2>
      </template>
    </table-v2>

    <div v-else class="uf-empty">
      <v2-icon icon="attach_file" :size="40" />
      <p>{{ $t('FILES.EMPTY_STATE') }}</p>
    </div>

    <modal-v2
      :show="showUploadModal"
      :on-close="closeModal"
      class="upload-file-modal"
    >
      <woot-modal-header-v2
        :header-title="$t('FILES.MODAL.TITLE')"
        :header-content="$t('FILES.MODAL.SUBTITLE')"
      >
        <template #titleIcon>
          <v2-icon icon="upload" class="text-gray-500" />
        </template>
      </woot-modal-header-v2>

      <div class="uf-modal-body">
        <div
          v-if="!attachedFile"
          class="mb-4 flex cursor-pointer flex-col items-center justify-center gap-3 rounded-[26px] border-2 border-dashed bg-white py-11 transition-colors"
          :class="
            isModalDragOver
              ? 'border-brand/40'
              : 'border-[#dfdcdc] hover:border-brand/40'
          "
          @click="triggerFileInput"
          @dragover.prevent="isModalDragOver = true"
          @dragleave="isModalDragOver = false"
          @drop.prevent="onModalDrop"
        >
          <div class="flex flex-col items-center gap-2 text-center">
            <div class="text-[16px] leading-[1.2] text-[#323431]">
              {{ $t('FILES.MODAL.DROPZONE_TITLE') }}
            </div>
            <div class="text-[14px] leading-[1.5] text-[#717171] mb-0">
              {{ $t('FILES.MODAL.MAX_SIZE') }}
            </div>
          </div>
        </div>

        <div
          v-else
          class="relative mb-4 flex w-full items-center gap-3 rounded-[12px] border border-[#e5e5e5] bg-white p-3"
        >
          <file-type-icon :file-name="attachedFile.name" :size="40" />
          <div class="flex min-w-0 flex-1 flex-col gap-1">
            <div class="truncate text-[14px] leading-5 text-[#323431]">
              {{ attachedFile.name }}
            </div>
            <div class="text-[12px] leading-[18px] text-[#5c5c5c]">
              {{ formattedFileSize }}
            </div>
          </div>
          <button
            type="button"
            class="shrink-0 cursor-pointer self-start text-[#6b7280] transition-colors hover:text-[#323431]"
            :title="$t('FILES.MODAL.REMOVE_FILE')"
            @click.stop="clearAttached"
          >
            <v2-icon icon="close" :size="16" />
          </button>
        </div>
        <input
          ref="fileInput"
          type="file"
          :accept="allowedFileTypes"
          class="uf-hidden-input"
          @change="onFileSelected"
        />

        <div class="uf-modal-field">
          <input-v2
            v-model.trim="comment"
            :label="$t('FILES.MODAL.COMMENT_LABEL')"
            :placeholder="$t('FILES.MODAL.COMMENT_PLACEHOLDER')"
          />
        </div>
      </div>

      <modal-footer-v2
        :cancel-text="$t('FILES.MODAL.CANCEL')"
        :confirm-text="$t('FILES.MODAL.UPLOAD')"
        :is-disabled="!attachedFile"
        :is-loading="uploading"
        @cancel="closeModal"
        @confirm="handleSubmit"
      />
    </modal-v2>

    <woot-delete-modal
      v-if="fileToDelete"
      v2
      :show="!!fileToDelete"
      :on-close="closeDeleteModal"
      :on-confirm="confirmRemove"
      :title="$t('FILES.DELETE_CONFIRM.TITLE')"
      :message="$t('FILES.DELETE_CONFIRM.MESSAGE')"
      :message-value="` ${getFileName(fileToDelete)}?`"
      :confirm-text="$t('FILES.DELETE_CONFIRM.YES')"
      :reject-text="$t('FILES.DELETE_CONFIRM.NO')"
      :is-loading="deletingFile"
    />
  </div>
</template>

<script>
import {
  ALLOWED_FILE_TYPES,
  MAXIMUM_FILE_UPLOAD_SIZE,
} from 'shared/constants/contact';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import { fileMatchesAccept } from 'v2/views/campaigns/wizard/helpers/templateHelpers';
import alertMixin from 'shared/mixins/alertMixin';
import rtlMixin from 'shared/mixins/rtlMixin';
import V2Icon from 'v2/components/Icon.vue';
import FileTypeIcon from 'v2/components/FileTypeIcon.vue';
import TableV2 from 'v2/components/Table/TableV2.vue';
import TableHeaderCellV2 from 'v2/components/Table/TableHeaderCellV2.vue';
import TableRowV2 from 'v2/components/Table/TableRowV2.vue';
import TableCellV2 from 'v2/components/Table/TableCellV2.vue';
import TableDropdownV2 from 'v2/components/Table/TableDropdownV2.vue';

export default {
  name: 'PrivateFilesOnContactPage',
  components: {
    V2Icon,
    FileTypeIcon,
    TableV2,
    TableHeaderCellV2,
    TableRowV2,
    TableCellV2,
    TableDropdownV2,
  },
  mixins: [alertMixin, rtlMixin],
  props: {
    contactId: {
      type: Number,
      required: true,
    },
    compact: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      comment: '',
      attachedFile: null,
      files: [],
      showUploadModal: false,
      isModalDragOver: false,
      editingFileId: null,
      editComment: '',
      uploading: false,
      fileToDelete: null,
      deletingFile: false,
      activeDropdownColumn: null,
      columnFilters: { file_name: '', comment: '' },
      sortConfig: {},
    };
  },
  computed: {
    columnAlign() {
      return this.isRTLView ? 'right' : 'left';
    },
    allowedFileTypes() {
      return ALLOWED_FILE_TYPES;
    },
    formattedFileSize() {
      if (!this.attachedFile) return '';
      const bytes = this.attachedFile.size;
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    },
    displayedFiles() {
      let result = this.files.slice();
      const nameQuery = (this.columnFilters.file_name || '')
        .trim()
        .toLowerCase();
      const commentQuery = (this.columnFilters.comment || '')
        .trim()
        .toLowerCase();
      if (nameQuery) {
        result = result.filter(file =>
          this.getFileName(file).toLowerCase().includes(nameQuery)
        );
      }
      if (commentQuery) {
        result = result.filter(file =>
          (file.comment || '').toLowerCase().includes(commentQuery)
        );
      }
      const sortEntry = Object.entries(this.sortConfig)[0];
      if (sortEntry) {
        const [field, order] = sortEntry;
        result.sort((a, b) => {
          const valA =
            field === 'file_name'
              ? this.getFileName(a).toLowerCase()
              : (a.comment || '').toLowerCase();
          const valB =
            field === 'file_name'
              ? this.getFileName(b).toLowerCase()
              : (b.comment || '').toLowerCase();
          if (valA < valB) return order === 'asc' ? -1 : 1;
          if (valA > valB) return order === 'asc' ? 1 : -1;
          return 0;
        });
      }
      return result;
    },
  },
  mounted() {
    this.fetchFiles();
  },
  methods: {
    async fetchFiles() {
      const { contactId: id } = this;
      this.files = await this.$store.dispatch('contacts/fetchFiles', { id });
    },
    triggerFileInput() {
      if (!this.attachedFile) {
        this.$refs.fileInput.click();
      }
    },
    onModalDrop(e) {
      this.isModalDragOver = false;
      const file = e.dataTransfer.files[0];
      if (file) {
        this.processFile(file);
      }
    },
    onFileSelected(e) {
      const file = e.target.files[0];
      if (file) {
        this.processFile(file);
      }
      this.$refs.fileInput.value = '';
    },
    processFile(file) {
      if (!fileMatchesAccept(ALLOWED_FILE_TYPES, file)) {
        this.showAlert(this.$t('FILES.ALERT.ERROR.FILE_TYPE'));
      } else if (checkFileSizeLimit({ file }, MAXIMUM_FILE_UPLOAD_SIZE)) {
        this.attachedFile = file;
      } else {
        this.showAlert(
          this.$t('FILES.ALERT.ERROR.FILE_SIZE', {
            size: MAXIMUM_FILE_UPLOAD_SIZE,
          })
        );
      }
    },
    clearAttached() {
      this.attachedFile = null;
    },
    closeModal() {
      this.showUploadModal = false;
      this.attachedFile = null;
      this.comment = '';
      this.isModalDragOver = false;
    },
    handleSubmit() {
      if (!this.attachedFile || this.uploading) return;
      this.onSubmit();
    },
    closeDeleteModal() {
      this.fileToDelete = null;
    },
    async confirmRemove() {
      if (!this.fileToDelete || this.deletingFile) return;
      this.deletingFile = true;
      try {
        this.files = await this.$store.dispatch('contacts/removeFile', {
          id: this.contactId,
          fileId: this.fileToDelete.id,
        });
        this.showAlert(this.$t('FILES.ALERT.SUCCESS.DELETE'));
        this.closeDeleteModal();
      } catch (error) {
        this.showAlert(error.message);
      } finally {
        this.deletingFile = false;
      }
    },
    async onSubmit() {
      this.uploading = true;
      try {
        this.files = await this.$store.dispatch('contacts/addFile', {
          id: this.contactId,
          isFormData: true,
          file: this.attachedFile,
          comment: this.comment,
          additional_attributes: { social_profiles: {} },
        });
        this.showAlert(this.$t('FILES.ALERT.SUCCESS.ADD'));
        this.closeModal();
      } catch (error) {
        this.showAlert(error.message);
      } finally {
        this.uploading = false;
      }
    },
    startEdit(file) {
      this.editingFileId = file.id;
      this.editComment = file.comment || '';
      this.$nextTick(() => this.$refs.commentInput?.[0]?.focus());
    },
    cancelEdit() {
      this.editingFileId = null;
      this.editComment = '';
    },
    async saveComment(file) {
      if (this.editingFileId !== file.id) return;
      const comment = this.editComment.trim();
      this.cancelEdit();
      if (comment === (file.comment || '')) return;
      try {
        this.files = await this.$store.dispatch('contacts/updateFile', {
          id: this.contactId,
          fileId: file.id,
          comment,
        });
        this.showAlert(this.$t('FILES.ALERT.SUCCESS.UPDATE'));
      } catch (error) {
        this.showAlert(this.$t('FILES.ALERT.ERROR.UPDATE'));
      }
    },
    toggleDropdown(key) {
      this.activeDropdownColumn =
        this.activeDropdownColumn === key ? null : key;
    },
    closeDropdown() {
      this.activeDropdownColumn = null;
    },
    setFilter(key, val) {
      this.columnFilters = { ...this.columnFilters, [key]: val };
    },
    getSortOptions() {
      return [
        {
          value: 'asc',
          label: this.$t('CONTACTS_PAGE.FILTER_CONTACTS_DROPDOWN.A_TO_Z'),
        },
        {
          value: 'desc',
          label: this.$t('CONTACTS_PAGE.FILTER_CONTACTS_DROPDOWN.Z_TO_A'),
        },
      ];
    },
    getCurrentSort(key) {
      return this.sortConfig[key] || '';
    },
    applySortFromDropdown(key, order) {
      this.sortConfig = { [key]: order };
      this.closeDropdown();
    },
    getFileName(file) {
      if (file.data_url) {
        const parts = file.data_url.split('/');
        return decodeURIComponent(parts[parts.length - 1]) || 'file';
      }
      if (file.file_name) return file.file_name;
      if (file.name) return file.name;
      return 'file';
    },
    getFileUrl(file) {
      return file.data_url || file.url || '';
    },
  },
};
</script>

<style lang="scss" scoped>
.upload-files-wrap {
  position: relative;
  min-height: 200px;
}

.upload-files-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.upload-files-title {
  font-size: 18px;
  font-weight: 600;
  color: #4b5563;
  margin: 0;
}

.uf-name-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}

.uf-filename-link {
  min-width: 0;
  color: #2563eb;
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
}

.uf-filename {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.uf-comment-td {
  color: #4b5563;
}

.uf-comment-input {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: #2abb7f;
  }
}

.uf-actions-td {
  padding-block: 4px !important;
}

.uf-actions {
  display: flex;
  gap: 8px;
  justify-content: center;

  .button-ghost {
    @apply w-11 h-11;
  }
}

.uf-no-match {
  color: #9ca3af;
}

.uf-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #9ca3af;

  p {
    margin: 12px 0 0;
    font-size: 14px;
  }
}

.upload-file-modal ::v-deep .modal-container.modal-v2 {
  width: 600px;
}

.uf-modal-body {
  padding: 16px 24px;
}

.uf-hidden-input {
  display: none;
}

.uf-modal-field {
  margin-bottom: 0;
}
</style>
